package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/flow_builders")
@Composable
fun FlowBuildersPage() {
    BlogLayout(
        PageProperties(
            "Flow Builders",
            "Flow builders are used to create instances of Flow which is a type that represents a cold asynchronous data stream that sequentially emits values and completes normally or with an exception."
        ),
        BlogProperties("Coroutines", "Flow Builders")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Coroutine flows are a type of cold stream in Kotlin, which means they do not start producing values until a collector starts collecting them. This is in contrast to hot streams, which might begin emitting values immediately upon creation.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Flow builders are used to create instances of Flow which is a type that represents a cold asynchronous data stream that sequentially emits values and completes normally or with an exception.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "flow()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "Flow When executed in the same coroutine, emission and use occur sequentially. This behavior slows down the overall execution time.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "return@flow",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "return@label syntax is used to return from a specific function or lambda, especially in contexts like higher-order functions or coroutines. Specifically, return@flow is often seen in coroutine flows. This can be useful in Flow-based data processing pipelines where you might need to stop collecting or emitting values under certain conditions. Stops the execution inside the flow block without exiting the enclosing function or coroutine.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "buffer()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "By default, Flow is sequential:",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )

                        Ul {
                            Li {
                                SpanText(
                                    text = "The emitter (emit) and the collector (collect) run in the same coroutine.",
                                    modifier = Modifier.padding(bottom = 0.3.em)
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li {
                                SpanText(
                                    text = "That means the next emission waits until the previous one is fully collected.",
                                    modifier = Modifier.padding(bottom = 0.3.em)
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }

                    Li {
                        SpanText(
                            text = "buffer() allows emissions and collections to work concurrently (decoupling producer & consumer).",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Using, emission and use can be made to operate in separate coroutines. Because data is produced in advance, stored in a buffer, and used immediately, unnecessary delays at execution time are eliminated.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Creates a channel with specified capacity to buffer emissions",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Processes all values eventually, ensuring no data loss",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Useful when processing speed varies between producer and consumer",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Memory usage increases with buffer size",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                        fun simpleFlow(): Flow<Int> = flow {
                            for (i in 1..3) {
                                delay(100) // pretend to fetch data
                                println("Emit $i")
                                emit(i)
                            }
                        }

                        runBlocking {
                            val time = measureTimeMillis {
                                simpleFlow().collect { value ->
                                    delay(300) // slow collector
                                    println("Collect $value")
                                }
                            }
                            println("Collected in $time ms")
                        }

                        // Output
                        Emit 1
                        Collect 1
                        Emit 2
                        Collect 2
                        Emit 3
                        Collect 3
                        Collected in ~1200 ms
                    """.trimIndent()
                )

                KotlinCode(
                    code = $$"""
                        runBlocking {
                            val time = measureTimeMillis {
                                simpleFlow()
                                    .buffer() // <-- decouple producer from consumer
                                    .collect { value ->
                                        delay(300) // slow collector
                                        println("Collect $value")
                                    }
                            }
                            println("Collected in $time ms")
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "launchIn()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "You can run it by specifying a different scope without buffer having to use .launchIn. launchIn is a terminal operator that launches the collection of the flow in the given CoroutineScope. It's used to start the flow and keep collecting its emissions within the specified scope until the scope is cancelled or the flow completes.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = ".launchIn() can be called like this in any regular function.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = ".launchIn() method, you also get a Job as a return value, so you could cancel the flow by canceling the job. The collection of the flow will be automatically cancelled when the scope is cancelled.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        flowOf(1, 2, 3)
                        .onEach { println(it) }
                        .launchIn(coroutineScope)
                        
                        //Job    
                        val job = flowOf(1, 2, 3)
                        .onEach { println(it) }
                        .launchIn(coroutineScope)
                        //job.cancel()
                        
                        runBlocking {
                            flowOf(1, 2, 3)
                                .onEach { delay(100) }
                                .onEach { println(it) }
                                .launchIn(this)
                            flowOf("a", "b", "c")
                                .onEach { println(it) }
                                .launchIn(this)
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.flow.*
                    fun main() = runBlocking {
                        val scope = CoroutineScope(Dispatchers.Default)
                        val flow = flow {
                            emit(1)
                            delay(1000)
                            emit(2)
                            delay(1000)
                            emit(3)
                        }
                        
                        //The onEach operator is used to print the received values.
                        flow
                            .onEach { value -> println(value) }
                            .launchIn(scope)
                        // Wait for some time to observe the flow emissions
                        delay(3000)
                        // Cancel the scope to stop collecting the flow. The scope is cancelled after a delay, stopping the flow collection.
                        scope.cancel()
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collect",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "Processes all values emitted by the flow. The new emission will be queued if the current emission collected is still running. Executes a lambda function for every value emitted by the upstream flow. When multiple collectors are attached to the same flow, each collector will receive all emitted values. If one collector is slower than the other(s), it might lag behind and process values emitted while it was busy.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "You are downloading a list of files, and every file must be downloaded completely. No file can be skipped or interrupted.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "To consume data emitted by a flow, we use the `collect` function, which suspends until the flow completes:",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                            import kotlinx.coroutines.delay
                            import kotlinx.coroutines.flow.Flow
                            import kotlinx.coroutines.flow.flow

                            fun produceNumbers(): Flow<Int> = flow {
                                for (i in 1..5) {
                                    delay(1000) // Simulate some asynchronous operation
                                    emit(i)     // Emit each integer in the sequence
                                }
                            }
                            
                            import kotlinx.coroutines.flow.collect

                            suspend fun main() {
                                produceNumbers().collect { value ->
                                    println(value) // Print each emitted value
                                }
                            }
                        """.trimIndent()
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "collectLatest",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "Processes only the latest value emitted by the flow. The new emission will replace the current running emission (if it exists). This is useful for handling UI updates as we are interested only in the latest state. Cancels any ongoing collection if a new value is emitted before the previous one finishes. Executes a lambda function only for the latest received value. When multiple collectors are attached to the same flow, if a new value is emitted while a collector is processing a previous value, the collector will stop processing the previous value and restart processing the new value.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "conflate()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li {
                        SpanText(
                            text = "After the current .collect{...} completes, when there are more than one emission queued, only the last emission in the queue will be collected.",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Keeps only the latest value when collector is slow",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Drops intermediate values between processed and newest",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Effectively similar to buffer(1) with CONFLATED behavior",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li {
                        SpanText(
                            text = "Optimal for scenarios where only the latest value matters",
                            modifier = Modifier.padding(bottom = 0.3.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    data class SearchState(
                        val query: String = "",
                        val results: List<SearchResult> = emptyList(),
                        val isLoading: Boolean = false,
                        val error: String? = null,
                        val searchFilters: SearchFilters = SearchFilters()
                    )

                    class SearchViewModel(
                        private val searchRepository: SearchRepository,
                        private val analyticsTracker: AnalyticsTracker
                    ) : ViewModel() {
                        private val _searchState = MutableStateFlow(SearchState())
                        val searchState = _searchState.asStateFlow()

                        init {
                            viewModelScope.launch {
                                combine(
                                    _searchState.map { it.query }
                                        .debounce(300)
                                        .distinctUntilChanged(),
                                    _searchState.map { it.searchFilters }
                                        .distinctUntilChanged()
                                ) { query, filters -> 
                                    SearchParams(query, filters) 
                                }
                                .flatMapLatest { searchParams ->
                                    flow {
                                        emit(SearchIntent.Loading)
                                        try {
                                            val results = searchRepository.search(
                                                query = searchParams.query,
                                                filters = searchParams.filters
                                            )
                                            emit(SearchIntent.Success(results))
                                        } catch (e: Exception) {
                                            emit(SearchIntent.Error(e.message ?: "Search failed"))
                                        }
                                    }
                                }
                                .collectLatest { intent ->
                                    updateSearchState(intent)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class ChatRepository(
                        private val chatApi: ChatApi,
                        private val chatDao: ChatDao,
                        private val networkMonitor: NetworkMonitor
                    ) {
                        private val messageQueue = Channel<ChatMessage>(Channel.UNLIMITED)
                        private val retryInterval = 5000L // 5 seconds

                        private fun processMessageQueue() {
                            CoroutineScope(Dispatchers.IO + SupervisorJob()).launch {
                                messageQueue
                                    .consumeAsFlow()
                                    .buffer(Channel.UNLIMITED)
                                    .collect { message ->
                                        processMessageWithRetry(message)
                                    }
                            }
                        }

                        private suspend fun processMessageWithRetry(message: ChatMessage) {
                            var currentAttempt = 0
                            val maxAttempts = 3

                            while (currentAttempt < maxAttempts) {
                                try {
                                    sendMessageToServer(message)
                                    break
                                } catch (e: Exception) {
                                    handleMessageError(message, currentAttempt, maxAttempts)
                                    currentAttempt++
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class LocationTracker(
                        private val context: Context,
                        private val fusedLocationClient: FusedLocationProviderClient,
                        private val locationDao: LocationDao,
                        private val batteryManager: BatteryManager
                    ) {
                        private val _locationUpdates = MutableSharedFlow<LocationUpdate>()
                        
                        fun startLocationTracking() {
                            viewModelScope.launch {
                                combine(
                                    batteryManager.batteryLevelFlow,
                                    _locationUpdates
                                ) { batteryLevel, location ->
                                    location.copy(batteryLevel = batteryLevel)
                                }
                                .conflate()
                                .collect { locationUpdate ->
                                    processLocationUpdate(locationUpdate)
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Handling Data Streams with Kotlin Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Kotlin, intermediate operators are the operators that are used to manipulate and transform data streams in functional programming constructs such as sequences, lists, and arrays.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These operators are typically used to perform transformations on the data stream, such as filtering, mapping, sorting, and aggregating.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Some of the intermediate operators in Kotlin include:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "distinctUntilChanged",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The distinctUntilChanged operator in Kotlin Flow is used to suppress consecutive duplicate emissions in the Flow.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It ensures that only distinct values are emitted, and any consecutive duplicates are skipped.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                    fun main() = runBlocking {
                        // Sample Flow of integers with consecutive duplicates
                        val numbers = flowOf(1, 2, 2, 3, 4, 4, 4, 5, 5)

                        // Applying the distinctUntilChanged operator to filter out consecutive duplicates
                        val distinctNumbers = numbers.distinctUntilChanged()

                        // Collect and print the distinct numbers
                        distinctNumbers.collect { number ->
                            println(number)
                        }
                    }

                    // Output
                    1
                    2
                    3
                    4
                    5
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    //Custom Data Objects with Equals Override
                        
                    data class Person(val id: Int, val name: String)

                    fun main() = runBlocking {
                        flowOf(
                            Person(1, "Alice"),
                            Person(1, "Alice"),
                            Person(2, "Bob"),
                            Person(2, "Bob"),
                            Person(3, "Charlie")
                        )
                        .distinctUntilChanged { old, new -> old.id == new.id }
                        .collect { println(it) } 
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    fun main() = runBlocking {
                        (1..10)
                            .asFlow()
                            .map { it % 3 } // Map to remainders: 1, 2, 0, 1, 2, 0, 1, 2, 0, 1
                            .distinctUntilChanged()
                            .collect { println(it) } 
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    fun main() = runBlocking {
                        flowOf(1, 1, 2, 2, 3)
                            .scan(emptyList<Int>()) { accumulator, value ->
                                accumulator + value
                            }
                            .distinctUntilChanged()
                            .collect { println(it) } // Output: [], [1], [1, 2], [1, 2, 3]
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    fun main() = runBlocking {
                        val flowA = flowOf(1, 1, 2, 2, 3)
                        val flowB = flowOf('a', 'b', 'b', 'c', 'c')
                        flowA.combine(flowB) { a, b -> Pair(a, b) }
                            .distinctUntilChanged()
                            .collect { println(it) } // Output: (1, a), (2, b), (3, c)
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    import kotlinx.coroutines.flow.*
                    import kotlinx.coroutines.runBlocking

                    data class User(val id: Int, val name: String)
                    fun main() = runBlocking {
                        val users = listOf(
                            User(1, "Alice"),
                            User(1, "Alice"),
                            User(2, "Bob"),
                            User(3, "Charlie"),
                            User(3, "Charlie")
                        ).asFlow()
                    users
                            .distinctUntilChangedBy { it.id }
                            .collect { println(it) }
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "flatMap",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Returns a single list of all elements yielded from results of applying certain condition on each element of original collection.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "flatMap() is usually useful for flattening one-to-many relationships.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                        fun <T, R> Iterable<T>.flatMap(transform: (T) -> Iterable<R>): List<R>
                    """.trimIndent()
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "T is the type of elements in the original collection.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "R represents the type of elements in the resulting collection.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "transform is a lambda function that maps each element of the original collection to an iterable of the desired type. It lets you emit zero, one, or many values for each upstream value — giving you fine-grained control over how data flows downstream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                }

                KotlinCode(
                    code = """
                    data class MotorVehicle(
                        val name: String,
                        val model: Int,
                        val manufacturer: String
                    )

                    val cars = listOf(
                        MotorVehicle("Swift", 2016, "Maruti"),
                        MotorVehicle("Altroz", 2020, "Tata"),
                        MotorVehicle("Verna", 2019, "Hyundai")
                    )
                    val bikes = listOf(
                        MotorVehicle("R-15", 2018, "Yamaha"),
                        MotorVehicle("Gixxer", 2017, "Suzuki")
                    )

                    val vehicles = listOf(cars, bikes)

                    //val allVehicles = vehicles.flatMap { it }

                    val manufacturerList = vehicles.flatMap {
                        it
                    }.map {
                        it.manufacturer
                    }
                    
                    fun main() {
                        val phrases = listOf("Welcome to", "Educative Answers", "Kotlin is used for mobile development.")
                        val words = phrases.flatMap { it.split(" ") }
                        println(words)
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    data class Comment(val text: String)
                    data class BlogPost(val title: String, val comments: List<Comment>)

                    fun main() {
                        val posts = listOf(
                            BlogPost("Kotlin Basics", listOf(Comment("Great post!"), Comment("Very informative."))),
                            BlogPost("Advanced Kotlin", listOf(Comment("Need more details."), Comment("Loved the tips!")))
                        )

                        val allComments = posts.flatMap { it.comments }.map { it.text }

                        println(allComments)  // [Great post!, Very informative., Need more details., Loved the tips!]
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        //flatMapLatest using check the value is contain or not
                        
                        @OptIn(ExperimentalCoroutinesApi::class)
                        fun isInFavoriteList(city: String): StateFlow<Boolean> =
                            repository.getFavorites().flatMapLatest {
                                flowOf(
                                    it.firstOrNull { it.city == city } != null
                                )
                            }.stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5_000),
                                initialValue = false
                            )
                         
                        fun getFavorites(): Flow<List<FavoriteModel>> = dao.getFavorites().map {
                            it.map(FavoriteEntity::toDomain)
                        }    
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "flatten",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This function is used to flatten map. On other words, flatMap{} is basically a combination of flatten() and map{}",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "take",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Takes the first n elements from the flow.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        flowOf(1, 2, 3, 4, 5)
                            .take(3) // Takes only the first 3 elements
                            .collect { println(it) } // Output: 1, 2, 3
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "drop",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Drops the first n elements and emits the rest",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        flowOf(1, 2, 3, 4, 5)
                            .drop(2) // Drops the first 2 elements
                            .collect { println(it) } // Output: 3, 4, 5
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "collectLatest",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cancels the collection of the current element if a new element is emitted.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        flow {
                            for (i in 1..3) {
                                delay(100)
                                emit(i)
                            }
                        }
                        .collectLatest { value ->
                            println(value) // Output: 1, 2, 3
                            delay(300) // Simulate work
                            println(value) // Output: 3
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "toList and toSet",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Converts the flow into a list or set.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        val intList = flowOf(1, 2, 3).toList() // list contains [1, 2, 3]
                        val intSet = flowOf(1, 2, 2, 3).toSet() // set contains [1, 2, 3]
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "single and singleOrNull",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Returns the single element from the flow, or throws an exception if the flow does not contain exactly one element (single). The singleOrNull variant returns null for empty flows or flows with more than one element.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        val singleValue = flowOf(1).single() // singleValue is 1
                        val singleOrNullValue = flowOf(1, 2).singleOrNull() // singleOrNullValue is null
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "reduce",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Reduce operator allows performing operation over all items of the flow to reduce them into one item.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        flowOf(1, 2, 3)
                        .reduce { acc, value -> acc + value }

                        //Output: 6  
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "flatMapConcat - (𝗢𝗻𝗲 𝗯𝘆 𝗢𝗻𝗲)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "flatMapConcat is used when you want to merge multiple flows into a single flow by sequentially concatenating them one after another. Processes inner flows sequentially, preserving order (next starts after previous completes).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It takes a function that transforms each emitted value of the source flow to another flow and then flattens the resulting flows.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This means that the downstream collector receives the values from the resulting flow in the same order they were emitted by the source flow, with each transformed flow's values emitted in the order they were emitted.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                    val flow1 = flowOf("A", "B", "C")
                    val flow2 = flowOf("D", "E", "F")
                    val flow3 = flowOf("G", "H", "I")

                    flowOf(flow1, flow2, flow3)
                        .flatMapConcat { it }
                        .collect {
                            println(it)
                        }

                    // Output: A B C D E F G H I
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "flatMapLatest - (𝗞𝗲𝗲𝗽 𝗢𝗻𝗹𝘆 𝘁𝗵𝗲 𝗟𝗮𝘁𝗲𝘀𝘁)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cancels the previous inner flow if a new value comes in.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Always works with the most recent data.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Ideal when older results become irrelevant quickly.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Handling live user input like a search bar.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "flatMapMerge - (𝗔𝗹𝗹 𝗮𝘁 𝗢𝗻𝗰𝗲)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Runs all inner flows concurrently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Doesn’t wait or cancel — just merges everything.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Does not preserve order, but gives faster results.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You need to perform multiple independent tasks in parallel.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "whileSubscribedIn",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This one is a hot Flow operator (from kotlinx.coroutines.flow extensions).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It’s usually used with SharedFlow or StateFlow inside viewModelScope..",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Starts upstream only while at least one subscriber is active, and cancels when no one is listening.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                        val scope = CoroutineScope(Dispatchers.Default)

                        val numbers = flow {
                            println("Producing numbers...")
                            emitAll((1..5).asFlow().onEach { delay(500) })
                        }.whileSubscribedIn(scope)

                        scope.launch {
                            numbers.collect { println("Collector 1: $it") }
                        }

                        delay(1200)

                        val job2 = scope.launch {
                            numbers.collect { println("Collector 2: $it") }
                        }

                        delay(2000)
                        job2.cancel()

                        delay(2000) // no active collectors, flow stops
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "fold",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                KotlinCode(
                    code = """
                            flowOf(1, 2, 3)
                                .fold("hello", { acc, value -> acc + value })
                                
                            //Output: hello123
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "debounce",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Prevents a network hit on every keystroke.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Debounce operator emits an item from a Flow only if a particular timespan has passed without it emitting another item. Only emits the last item from items emitted consecutively within a specific time period.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For example, consider you are designing a search box and you have a flow of user input.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        //debounce each item with 1000 second
                        flow.debounce(1000L)
                        .collect {
                            //collect each item after 1 second
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Sample",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Similar to debounce, it’s also used to filter items from the Flow but it has one important difference — Instead of checking interval from the last item, sample operator will run periodically and will send 1 latest item per interval.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Remember, if we had used debounce here, we will only get item 9.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "map",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This operator applies a transformation function to each element in the data stream and returns a new stream with the transformed values.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        val numbersFlow = flowOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10)

                        numbersFlow
                            .map { it * it }
                            .collect { println(it) }  // prints 1, 4, 9, 16, 25, 36, 49, 64, 81, 100
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "filter",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This operator filters out elements from the data stream that do not meet a specified condition and returns a new stream with only the filtered values. Removes items from the flow that don’t satisfy a specific condition (predicate).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        val numbersFlow = flowOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10)

                        numbersFlow
                            .filter { it % 2 == 0 }
                            .collect { println(it) }  // prints 2, 4, 6, 8, 10
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "flatMap",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This operator applies a transformation function that returns a stream to each element in the data stream, flattening the resulting streams into a single output stream.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "distinct",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This operator removes duplicates from the data stream, returning a new stream with only unique elements.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "transformLatest",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If you want to cancel the previous transformation when a new value comes (like switchMap), use transformLatest.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                         flowOf(1, 2, 3)
                             .transformLatest { value ->
                                 emit("Processing $value")
                                 delay(1000) // simulate work
                                 emit("Done $value")
                             }
                             .collect { println(it) }
                    """.trimIndent()
                )

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "sorted",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This operator sorts the data stream based on a specified comparator or natural ordering and returns a new stream with the sorted values.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "groupBy",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This operator groups the elements in the data stream based on a specified key selector function and returns a map with the keys and the corresponding groups of elements.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    data class Product(val name: String, val category: String, val price: Double, val sold: Int)

                    val products = listOf(
                        Product("A", "Books", 100.0, 10),
                        Product("B", "Books", 120.0, 5),
                        Product("C", "Books", 80.0, 20),
                        Product("D", "Gadgets", 200.0, 15),
                        Product("E", "Gadgets", 500.0, 3),
                        Product("F", "Gadgets", 150.0, 10)
                    )

                    val top3RevenueByCategory = products
                        .groupBy { it.category }
                        .mapValues { (_, list) ->
                            list.sortedByDescending { it.price * it.sold }.take(3)
                        }

                    println(top3RevenueByCategory)
                    """.trimIndent()
            )

            SpanText(
                text = "Handling Flow completion",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = ".onStart {}: You run some action before the upstream Flow starts emitting values. Emit a loading state before making a network/database call.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = ".onEach {}",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = ".onCompletion { exception -> } : exception is Throwable? corresponding to the exception thrown by the Flow – not being handled by .catch {}",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    viewModel.getSomeOtherItems(someValue)
                    .onStart { setProgressBarVisible() }
                    .onEach { items -> doSomething(items) }
                    .onCompletion { exception -> 
                        exception?.let { handleException(exception) }
                        setProgressBarInvisible()
                    }
                    .catch { exception -> handleException(exception) }
                    .collect()
                    """.trimIndent()
                )
            }

            SpanText(
                text = "collect()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "call collect() method only from another suspending function or from a coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It suspends coroutine, until it is finished doing its own thing. So in case of our code snipped, the coroutine is suspended on every delay that occurs and the coroutine will not continue executing any other code, until the first flow has been collected. That means, that the second flow won’t be collected at all, until first flow is completed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    coroutineScope.launch {
                        flowOf(1, 2, 3)
                            .collect { println(it) }
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Flow context",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A flow’s collection always runs in the context of the calling coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "by default flow{} code in a builder runs in the context provided by the corresponding flow’s collector.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    withContext(context) {
                        simple().collect { value -> 
                            println(value) // run in the specified context
                        }
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Cancelling Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flows are built on top of coroutines, it is not surprising to know they can be cancelled, and can have exceptions to handle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flows created using the flow{} builder are cancellable by default.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Each emit calls ensureActive internally to throw CancellationException if the coroutine is not active at the time of the call.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flows created using .asFlow() , and flowOf are not cancellable by default.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Add .cancellable() to make it cancellable. This will make emit to call ensureActive",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    suspend fun createAccount(email: String, password: String): FirebaseUser? {
                        return suspendCancellableCoroutine { cancellableContinuation ->
                            firebaseAuth.createUserWithEmailAndPassword(email, password).addOnSuccessListener {
                                val user = it.user
                                cancellableContinuation.resume(user)
                            }.addOnFailureListener {
                                cancellableContinuation.resumeWithException(it)
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Retrying Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can use retry and retryWhen",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We have to specify the predicate (i.e. the code block to specify the conditions for retry to be true) for both usages.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can emit a value to the Flow in the predicate",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    fun getSomeItems(id: Int) =
                    someRepository.getSomeItems(id)
                        .retry(2) {
                            // predicate
                            cause -> cause is IOException
                        }
                    """.trimIndent()
            )

            SpanText(
                text = "Catching an Exception",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Follow the traditional try-catch wrapping the collect function call",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use .catch {} Flow operator from the upstream flow (ie. the function or operators before the catch operator were called.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can call emit in the catch block",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We cannot use this to handle the exception within the code block of .collect {} / terminal operators. We have to move the code block to .onEach {} and place .catch {} between it and the .collect() call.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    viewModel.getSomeItems(someValue)
                    .catch { exception ->
                        emit("some message")
                        handleException(exception)
                    }
                    .collect { items -> doSomething(items) }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    viewModel.getSomeOtherItems(someValue)
                    .onEach {  items -> doSomething(items) }
                    .catch { exception -> handleException(exception) }
                    .collect()
                    """.trimIndent()
            )

            SpanText(
                text = "Exception Handling catch Operator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " The catch operator in Flow allows you to handle exceptions by encapsulating the error-handling logic in one place.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     flow {
                         emit(productsService.fetchProducts())
                     }.catch { e ->
                         emitError(e)
                     }
                """.trimIndent()
            )

            SpanText(
                text = "Custom Error Handling ",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In complex scenarios in Android, we can create custom operators or extension functions to handle errors in a way that suits our application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    fun <T> Flow<T>.sampleErrorHandler(): Flow<Result<T>> = transform { value ->
                        try {
                            emit(Result.Success(value))
                        } catch (e: Exception) {
                            emit(Result.Error(e))
                        }
                    }
                """.trimIndent()
            )

            SpanText(
                text = "onCompletion",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Executing code after the Flow completes, whether it completes successfully or with an exception
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The onCompletion operator is used for executing side-effects such as logging or cleanup operations. It has a nullable Throwable parameter which indicates whether the flow completed normally or exceptionally.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    flow {
                        emit(1)
                        throw RuntimeException("Error")
                    }.onCompletion { cause ->
                        if (cause != null) println("Flow completed exceptionally")
                    }.catch { e ->
                        println(e.message)
                    }.collect {
                        println(it)
                    }
                    // Output: 1, Flow completed exceptionally, Caught exception: Error
                    """.trimIndent()
            )

            SpanText(
                text = "retryWhen",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Similar to retry, but it provides more control. You can decide whether to retry and also modify the flow based on the exception.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    flow {
                        emit(doSomethingRisky())
                    }.retryWhen { cause, attempt ->
                        delay(1000) // Wait before retrying
                        attempt < 3 && cause is IOException
                    }.collect {
                        doSomethingElse(it)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Complex Data Transformations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun analyzePrices(symbol: String): Flow<Double> = 
                        fetchStockPrices(symbol)
                        .buffer(50) // Handle backpressure from slow processing
                        .runningReduce { acc, price -> (acc + price) / 2 } // Moving average
                        .map { average -> round(average * 100) / 100 } // Round to 2 decimals
                        .catch { e -> logError(e) } // Handle errors
                    """.trimIndent()
            )

            SpanText(
                text = "Asynchronous Database Storage",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun saveToDatabase(): Flow<Unit> = 
                        analyzePrices("")
                            .map { average ->
                                withContext(Dispatchers.IO) { // Async DB write
                                database.insertStockAverage("", "")
                            }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Lifecycle-Independent Processing",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // In a background service or repository:
                    val analyticsScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
                    
                    fun startPipeline() {
                        analyticsScope.launch {
                            saveToDatabase()
                                .collect() // Start the pipeline
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Converting Callbacks to Suspending Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "suspendCoroutine: This allows wrapping a callback-based function inside a coroutine. In the example below, asyncOperationWithCallback triggers a callback after a delay, and using suspendCoroutine, I resumed the coroutine with the result once the callback completes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "callbackFlow: Alternatively, converting a callback-based function into a reactive flow with callbackFlow allows multiple emissions and can clean up resources using awaitClose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Channel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channels are a great fit to model data sources that are intrinsically hot, data sources that exist without application’s requests for them: incoming network connections, event streams, etc.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ChannelFlows can have multiple coroutines running inside and could produce data from outside the stream.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Channels, just like futures, are synchronization primitives. You shall use a channel when you need to send data from one coroutine to another coroutine in the same or in a different process.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}

//https://medium.com/@shivayogih25/kotlin-flow-operators-complete-guide-with-use-cases-995270effdc9